import { HostListener, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as CryptoJS from 'crypto-js';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import { Subject } from 'rxjs';
import { GlobalConstants } from 'src/app/common/global-constants';
import { ClientProfileComponent } from 'src/app/modules/dash/shared/components/client-profile/client-profile.component';
import { ConfirmComponent } from 'src/app/modules/dash/shared/components/confirm/confirm.component';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { FileManagementComponent } from '../../modules/dash/shared/components/file-management/file-management.component';
import { UserProfileComponent } from '../../modules/dash/shared/components/user-profile/user-profile.component';

const app = initializeApp(environment.firebase);

@Injectable({
  providedIn: 'root',
})
export class PublicService {
  public isMobile = false;
  public numOfRows: number;
  public innerHeight: number;
  private storageKey = `${environment.prefix}-data`;
  private tempStorageKey = `${environment.prefix}-temp-data`;
  private encryptionKey = `${environment.prefix}-key`;
  private deviceIdKey = `${environment.prefix}-device-id`;
  public onlineStatus: Subject<boolean> = new Subject<boolean>();

  @HostListener('window:resize', ['$event'])
  onResize() { }

  constructor(
    private ngbModal: NgbModal,
    private sanitizer: DomSanitizer,
  ) {
    this.innerHeight = window.innerHeight;
    this.isMobile = window.innerWidth < 768;
    this.onlineStatus.next(navigator.onLine);
    window?.addEventListener('online', () => {
      this.onlineStatus.next(true);
    });

    window?.addEventListener('offline', () => {
      this.onlineStatus.next(false);
    });
  }

  public getActiveUserKey(): string {
    return localStorage.getItem(this.tempStorageKey)
      ? this.tempStorageKey
      : this.storageKey;
  }

  public isImpersonating(): boolean {
    return !!localStorage.getItem(`${environment.prefix}-temp-data`);
  }

  public exitImpersonation(): void {
    localStorage.removeItem(`${environment.prefix}-temp-data`);
    localStorage.removeItem(`${environment.prefix}-temp-permissions`);
    window.location.reload();
  }

  public getDeviceId(): string {
    let deviceId = localStorage.getItem(this.deviceIdKey);
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem(this.deviceIdKey, deviceId);
    }
    return deviceId;
  }

  async getFCMToken(): Promise<string | null> {
    try {
      const messaging = getMessaging(app);
      const token = await getToken(messaging, {
        vapidKey: environment.firebase.vapidKey,
      });

      if (token) {
        localStorage.setItem(`${environment.prefix}-fcm-token`, token);
        return token;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  public isClientLoggedIn(): boolean {
    const user = this.getMyData();
    return user?.auth_type === 2;
  }

  public isDisplayableImage(type: string | null | undefined): boolean {
    if (!type) return false;
    const unsupportedTypes = [
      'psd',
      'tiff',
      'heic',
      'heif',
      'x-icon',
      'bmp',
      'vnd.adobe.photoshop',
    ];
    return (
      type.startsWith('image/') &&
      !unsupportedTypes.some((ext) => type.includes(ext))
    );
  }

  public getPriorityLevel(
    status: number,
  ): { value: number; label: string; colorClass: string } | null {
    const levelObj = GlobalConstants.PriorityLevels.find(
      (item) => item.value === status,
    );
    return levelObj || null;
  }

  public getDifficultyLevel(
    level: number,
  ): { value: number; label: string; colorClass: string } | null {
    const levelObj = GlobalConstants.DifficultyLevels.find(
      (item) => item.value === level,
    );
    return levelObj || null;
  }

  public getTaskStatus(
    status: number,
  ): { value: number; label: string; colorClass: string } | null {
    const statusObj = GlobalConstants.TaskStatuses.find(
      (item) => item.value === status,
    );
    return statusObj || null;
  }

  public getQuoteStatus(
    status: number,
  ): { value: number; label: string; colorClass: string } | null {
    const statusObj = GlobalConstants.QuoteStatuses.find(
      (item) => item.value === status,
    );
    return statusObj || null;
  }

  public getStoryType(
    status: number,
  ): { value: number; label: string; colorClass: string } | null {
    const typeObj = GlobalConstants.StoryTypes.find(
      (item) => item.value === status,
    );
    return typeObj || null;
  }

  public getStoryStatus(
    status: number,
  ): { value: number; label: string; colorClass: string } | null {
    const statusObj = GlobalConstants.StoryStatuses.find(
      (item) => item.value === status,
    );
    return statusObj || null;
  }

  public getContractStatus(
    status: number,
  ): { value: number; label: string; colorClass: string } | null {
    const statusObj = GlobalConstants.ContractStatuses.find(
      (item) => item.value === status,
    );
    return statusObj || null;
  }

  public getPriorityColorClass(priority: number): string {
    switch (priority) {
      case 1:
        return 'text-danger';
      case 2:
        return 'text-danger';
      case 3:
        return 'text-warning';
      case 4:
        return 'text-success';
      case 5:
        return 'text-custom';
      default:
        return 'text-secondary';
    }
  }

  public getPriorityLabel(priority: number): string {
    switch (priority) {
      case 1:
        return 'Urgent';
      case 2:
        return 'VeryHigh';
      case 3:
        return 'High';
      case 4:
        return 'Medium';
      case 5:
        return 'Low';
      default:
        return 'Unknown Priority';
    }
  }

  public getMeetingStatus(
    status: number,
  ): { value: number; label: string; colorClass: string } | null {
    // 10 = NotStarted, 20 = InProgress, 30 = Finished
    const statusMap: {
      [key: number]: { value: number; label: string; colorClass: string };
    } = {
      10: {
        value: 10,
        label: 'NotStarted',
        colorClass: 'badge bg-primary-soft',
      },
      20: { value: 20, label: 'InProgress', colorClass: 'badge bg-info-soft' },
      30: { value: 30, label: 'Finished', colorClass: 'badge bg-success-soft' },
    };
    return statusMap[status] || null;
  }

  public getMyData() {
    const encryptedData = localStorage.getItem(this.getActiveUserKey());
    const data = encryptedData ? this.decryptData(encryptedData) : null;

    return data ? data.data : null;
  }

  public isUserInteractionDisabled(): boolean {
    return this.hasAnyPermission(['DISABLE_USER_INTERACTION']);
  }
  
  public getPermissions() {
    const encryptedData = localStorage.getItem(this.getActiveUserKey());
    const data = encryptedData ? this.decryptData(encryptedData) : null;
    return data ? data.permissions : null;
  }

  public hasAnyPermission(permissions: string[]): boolean {
    const encryptedData = localStorage.getItem(this.getActiveUserKey());
    const data = encryptedData ? this.decryptData(encryptedData) : null;
    const storedPermissions = data?.permissions;

    return permissions?.some((permission) =>
      storedPermissions?.includes(permission),
    );
  }

  public hasAllPermissions(permissions: string[]): boolean {
    const encryptedData = localStorage.getItem(this.getActiveUserKey());
    const data = encryptedData ? this.decryptData(encryptedData) : null;
    const storedPermissions = data?.permissions;

    return permissions?.every((permission) =>
      storedPermissions?.includes(permission),
    );
  }

  public getNumOfRows(): number {
    const rowHeight = 75; // Fixed row height for all tables
    this.numOfRows = Math.max(
      1,
      Math.floor((window.innerHeight - 280) / rowHeight),
    );
    return this.numOfRows;
  }

  public encryptData(data: any): string {
    return CryptoJS.AES.encrypt(
      JSON.stringify(data),
      this.encryptionKey,
    ).toString();
  }

  public decryptData(data: string): any {
    try {
      const bytes = CryptoJS.AES.decrypt(data, this.encryptionKey);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (error) {
      return null;
    }
  }

  public openImage(title: string, path: string) {
    const modalRef = this.ngbModal.open(FileManagementComponent, {
      fullscreen: true,
      windowClass: 'modal-image',
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.path = path;
  }

  public openUserProfile(userId: number) {
    if (userId != 0 && userId && this.hasAnyPermission(['VIEW_USER_DETAILS'])) {
      const modalRef = this.ngbModal.open(UserProfileComponent, {
        centered: true,
      });
      modalRef.componentInstance.userId = userId;
    }
  }

  public openClientProfile(clientId: number) {
    if (
      clientId != 0 &&
      clientId &&
      this.hasAnyPermission(['VIEW_CLIENT_DETAILS'])
    ) {
      const modalRef = this.ngbModal.open(ClientProfileComponent, {
        centered: true,
      });
      modalRef.componentInstance.clientId = clientId;
    }
  }

  public formatLinks(text: string): string {
    if (!text) return '-';

    const smartLinkRegex = /(https?:\/\/[^\s()<>"']+)\(([^()\n\r]{1,100})\)/g;
    const plainUrlRegex = /(https?:\/\/[^\s()<>"']+)/g;

    let formatted = text;
    const replacements: string[] = [];

    formatted = formatted.replace(smartLinkRegex, (_m, url, label) => {
      const cleanLabel = String(label)
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const id = replacements.length;
      replacements.push(
        `<a href="${url}" target="_blank" rel="noopener noreferrer" style="text-decoration:underline;word-break:break-word;">${cleanLabel}</a>`,
      );
      return `@@@URL_ID_${id}@@@`;
    });

    formatted = formatted.replace(plainUrlRegex, (url) => {
      const id = replacements.length;
      replacements.push(
        `<a href="${url}" target="_blank" rel="noopener noreferrer" style="text-decoration:underline;word-break:break-word;">${url}</a>`,
      );
      return `@@@URL_ID_${id}@@@`;
    });

    formatted = formatted.replace(/\n/g, '<br>');

    for (let i = 0; i < replacements.length; i++) {
      formatted = formatted.replace(`@@@URL_ID_${i}@@@`, replacements[i]);
    }

    return formatted;
  }

  public async confirm(
    title?: string,
    message?: string | null,
    confirmText?: string,
    cancelText?: string,
  ): Promise<boolean> {
    const modalRef = this.ngbModal.open(ConfirmComponent, {});
    modalRef.componentInstance.title = title ?? 'Confirm Action';
    modalRef.componentInstance.message = message ?? 'Are you sure ?';
    modalRef.componentInstance.confirmText = confirmText ?? 'Yes';
    modalRef.componentInstance.cancelText = cancelText ?? 'Cancel';

    return modalRef.result.then((res: boolean) => !!res).catch(() => false);
  }
}
